import React from 'react';
import { Switch } from 'antd';
import HelpOutlineIcon from 'assets/help_outline.svg?react';

interface FormToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  toggleLabel: string;
  helpText?: string;
  onHelpClick?: () => void;
  size?: 'small' | 'default';
  disabled?: boolean;
  className?: string;
  checkedChildren?: React.ReactNode;
  unCheckedChildren?: React.ReactNode;
  helpIcon?: React.ReactNode;
  'data-testid'?: string;
}

/**
 * FormToggle - Reusable toggle component for form sections
 *
 * Usage:
 * <FormToggle
 *   checked={enabled}
 *   onChange={setEnabled}
 *   toggleLabel="Auto-fetch data"
 *   helpText="what is this?"
 *   onHelpClick={() => showModal()}
 *   checkedChildren="ON"
 *   unCheckedChildren="OFF"
 *   helpIcon={<CustomIcon width={16} height={16} />} // Optional: defaults to HelpOutlineIcon
 * />
 */
const FormToggle: React.FC<FormToggleProps> = ({
  checked,
  onChange,
  toggleLabel,
  helpText,
  onHelpClick,
  size = 'default',
  disabled = false,
  className = '',
  checkedChildren,
  unCheckedChildren,
  helpIcon = <HelpOutlineIcon width={18} height={18} />,
  'data-testid': dataTestId
}) => {
  const handleHelpClick = () => {
    if (onHelpClick) {
      onHelpClick();
    }
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Switch
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(dataTestId && { 'data-testid': dataTestId })}
        checked={checked}
        onChange={onChange}
        size={size}
        disabled={disabled}
        checkedChildren={checkedChildren}
        unCheckedChildren={unCheckedChildren}
      />
      <span className="text-base font-semibold uppercase text-neutral-60">
        {toggleLabel}
      </span>
      {helpText && (
        <button
          type="button"
          className="flex items-center justify-center gap-1 text-sm font-normal leading-5 text-blue-500 cursor-pointer hover:underline bg-transparent border-none p-0"
          onClick={handleHelpClick}
        >
          {helpIcon}
          <span style={{ marginBottom: '3px' }}>{helpText}</span>
        </button>
      )}
    </div>
  );
};

export default FormToggle;
